import { Flex } from "antd"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"

import { nextPageUrl } from "../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../store.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import type {
  DecisionFormData,
  DeletionRequestFilters
} from "../api/services.type.ts"
import { setFilters } from "../redux/account-deletion.slice.ts"
import {
  approveDeletionRequestThunk,
  fetchDeletionRequests,
  rejectDeletionRequestThunk,
  retryDeletionRequestThunk
} from "../redux/account-deletion.thunks.ts"
import type { AccountDeletionRequest } from "../types/types.ts"
import { AccountDeletionRequestsHeader } from "./AccountDeletionRequestsHeader.tsx"
import { DecisionModal } from "./DecisionModal.tsx"
import { RequestsFilters } from "./RequestsFilters.tsx"
import { RequestsTable } from "./RequestsTable/RequestsTable.tsx"

const mapStateToProps = (state: RootState) => ({
  requests: state.accountDeletion.requests,
  fetching: state.accountDeletion.fetching,
  deciding: state.accountDeletion.deciding,
  filters: state.accountDeletion.filters,
  total: state.accountDeletion.pagination.count,
  next: state.accountDeletion.next,
  retryNext: state.accountDeletion.retryNext,
  canManage:
    !!state.auth.userInfo?.permissions?.codes?.team_management?.features
      ?.team_manage_account_deletion
})

const mapDispatchToProps = {
  fetch: fetchDeletionRequests,
  approve: approveDeletionRequestThunk,
  reject: rejectDeletionRequestThunk,
  retry: retryDeletionRequestThunk,
  changeFilters: setFilters
}

const connector = connect(mapStateToProps, mapDispatchToProps)

const AccountDeletionRequestsPage = ({
  requests,
  fetching,
  deciding,
  filters,
  total,
  next,
  retryNext,
  canManage,
  fetch,
  approve,
  reject,
  retry,
  changeFilters
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const [active, setActive] = useState<{
    mode: "approve" | "reject"
    row: AccountDeletionRequest
  } | null>(null)

  useEffect(() => {
    // Page one, always: `page` is persisted with the filters, and an
    // infinite list has no way back to the rows a restored page skipped.
    fetch({ ...filters, page: 1 })
  }, [fetch, filters])

  const loadMoreRequests = useCallback(() => {
    const url = nextPageUrl({ next, retryNext })
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    fetch({
      ...extractParamsFromNextUrl<DeletionRequestFilters>(url),
      isMore: true
    })
  }, [next, retryNext, fetch])

  const handleSubmit = (data: DecisionFormData) => {
    if (!active) return
    const payload = { id: active.row.id, admin_comment: data.admin_comment }
    if (active.mode === "approve") {
      approve({
        ...payload,
        successMessage: t("accountDeletionRequests.toast.approved")
      })
    } else {
      reject({
        ...payload,
        successMessage: t("accountDeletionRequests.toast.rejected")
      })
    }
    setActive(null)
  }

  return (
    <div className={"grid size-full"}>
      <Flex vertical gap={"middle"} className={"size-full overflow-auto"}>
        <AccountDeletionRequestsHeader />
        <RequestsFilters
          status={filters.status}
          q={filters.q}
          onSearchChange={(q) => changeFilters({ q, page: 1 })}
          onStatusChange={(status) =>
            changeFilters({ status: status ?? undefined, page: 1 })
          }
        />
        <RequestsTable
          data={requests}
          loading={fetching}
          canManage={canManage}
          deciding={deciding}
          onApprove={(row) => setActive({ mode: "approve", row })}
          onReject={(row) => setActive({ mode: "reject", row })}
          onRetry={(row) =>
            retry({
              id: row.id,
              successMessage: t("accountDeletionRequests.toast.retried")
            })
          }
          next={next}
          retryNext={retryNext}
          total={total}
          onLoadMore={loadMoreRequests}
        />
      </Flex>
      {active && (
        <DecisionModal
          open
          mode={active.mode}
          request={active.row}
          loading={deciding}
          onCancel={() => setActive(null)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  )
}

export default connector(AccountDeletionRequestsPage)
